import { FormEvent, useState } from 'react';
import { useTranslations } from 'next-intl';

import { urls } from '@zapier/identity-utils';
// By path: @zapier/authentication doesn't export these.
import passwordValidation, {
  minimumPasswordValidationMessage,
  mustHaveLetterValidationMessage,
  repeatedCharacterValidationMessage,
} from '@zapier/authentication/src/components/SignUpForm/passwordValidation';
import { fetch } from '../../../utils';

import { parseHash } from './parseHash';

type Input = {
  hash: string;
};

type ResetPasswordStatus = 'initial' | 'submitting' | 'success';

/** Messages PasswordRequirements shows as rows; any other needs text in the status region. */
const RULES_WITH_A_ROW = [
  minimumPasswordValidationMessage,
  mustHaveLetterValidationMessage,
  repeatedCharacterValidationMessage,
];

type ResetPasswordData = {
  password1: string;
  password2: string;
};

type ResetPasswordResult = {
  errors?: string[];
};

type ResetPasswordErrorCode = 'invalid' | 'too-frequent' | 'unknown';

type ResetPasswordError =
  | { kind: 'code'; code: ResetPasswordErrorCode }
  | { kind: 'server'; message: string };

async function submitResetPassword(
  data: ResetPasswordData,
  hash: string
): Promise<ResetPasswordError | ''> {
  const { code, username } = parseHash(hash);

  const resp = await fetch<ResetPasswordResult>(urls.RESET_PASSWORD_API_URL, {
    method: 'POST',
    body: {
      ...data,
      code,
      username,
    },
  });

  if (resp.body.errors) {
    return { kind: 'server', message: resp.body.errors[0] };
  }
  if (resp.status === 200) return '';
  if (resp.status === 400 || resp.status == 401 || resp.status === 403) {
    return { kind: 'code', code: 'invalid' };
  }
  if (resp.status == 429) return { kind: 'code', code: 'too-frequent' };
  return { kind: 'code', code: 'unknown' };
}

export function useResetPassword({ hash }: Input) {
  const t = useTranslations('reset-password');
  const [formData, setFormData] = useState<ResetPasswordData>({
    password1: '',
    password2: '',
  });
  const [error, setError] = useState<string | undefined>();
  const [status, setStatus] = useState<ResetPasswordStatus>('initial');
  const [hasLeftConfirmPassword, setHasLeftConfirmPassword] = useState(false);
  const [refusedSubmitCount, setRefusedSubmitCount] = useState(0);

  const passwordValidationResult = passwordValidation(formData.password1, true);
  const passwordErrors =
    typeof passwordValidationResult === 'string'
      ? passwordValidationResult
      : undefined;

  const passwordErrorMessages = passwordErrors ? passwordErrors.split(';') : [];

  const passwordsMatch =
    formData.password1.length > 0 && formData.password1 === formData.password2;

  const isSubmittable = passwordsMatch && !passwordErrors;

  const hasRefusedSubmit = refusedSubmitCount > 0;

  const unmetRuleMessages = hasRefusedSubmit
    ? passwordErrorMessages
    : passwordErrorMessages.filter(
        message => !RULES_WITH_A_ROW.includes(message)
      );

  const confirmPasswordError =
    formData.password1 !== formData.password2 &&
    (hasRefusedSubmit ||
      (hasLeftConfirmPassword && formData.password2.length > 0))
      ? t('errors.mismatch')
      : undefined;

  function resultToMessage(result: ResetPasswordError | ''): string {
    if (result === '') return '';
    if (result.kind === 'server') return result.message;
    switch (result.code) {
      case 'invalid':
        return t('errors.invalid');
      case 'too-frequent':
        return t('errors.too-frequent');
      case 'unknown':
        return t('errors.unknown');
    }
  }

  async function handleSubmit(e?: FormEvent<HTMLFormElement>) {
    if (e) {
      e.preventDefault();
    }

    if (status !== 'initial') return;

    // The button is aria-disabled, so click and Enter both land here: the gate lives here.
    if (!isSubmittable) {
      setRefusedSubmitCount(count => count + 1);
      return;
    }

    setStatus('submitting');
    setError(undefined);

    const error = resultToMessage(await submitResetPassword(formData, hash));

    setError(error);
    setStatus(error ? 'initial' : 'success');
  }

  function handleChange(field: string, value: string) {
    setHasLeftConfirmPassword(false);
    setRefusedSubmitCount(0);
    setFormData(data => ({
      ...data,
      [field]: value,
    }));
  }

  function handleConfirmPasswordBlur() {
    setHasLeftConfirmPassword(true);
  }

  return {
    // Drives aria-disabled only; the button stays pressable (see handleSubmit).
    canSubmit: isSubmittable && status === 'initial',
    confirmPasswordError,
    error,
    formData,
    handleChange,
    handleConfirmPasswordBlur,
    handleSubmit,
    passwordErrors,
    passwordsMatch,
    refusedSubmitCount,
    status,
    unmetRuleMessages,
  };
}
